<template>
  <DatePicker
    ref="datepicker"
    :type="type"
    :format="localeFormat"
    :disabled-date="disabledDate"
    :disabled-time="disabledTime"
    :clearable="false"
    :input-class="inputClass"
    :lang="lang"
    :minute-step="15"
    :open.sync="open"
    @change="change"
    :value-type="valueType"
    v-model="date"
    popup-class="mx-content-time"
  />
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/en';
import * as ptBR from 'vue2-datepicker/locale/pt-br';
import dayjs from '../../core/dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const lang = {
  ...ptBR,
  formatLocale: {
    ...ptBR.formatLocale,
    weekdaysMin: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'],
    monthsShort: [
      'jan',
      'fev',
      'mar',
      'abr',
      'mai',
      'jun',
      'jul',
      'ago',
      'set',
      'out',
      'nov',
      'dez',
    ],
  },
};

DatePicker.locale('pt-br', lang);

export default {
  components: {
    DatePicker,
  },

  props: {
    value: {
      default: null,
    },
    inputClass: {
      type: String,
      default: 'form-control',
    },
    type: {
      type: String,
      default: 'date',
    },
    future: {
      type: Boolean,
      default: true,
    },
    users: {
      type: Array,
      default: () => []
    },
    dealId: {
      type: [String, Number],
      default: null
    },
    leadHashid: {
      type: String,
      default: null
    }
  },

  watch: {
    open(newValue) {
      if (newValue) {
        this.scheduledActivities = {};
        this.loadScheduledActivities(this.date);
      }
    },
  },

  data() {
    return {
      date: this.value ? this.value : new Date(),
      lang: BulldeskSettings.locale === 'pt_BR' ? 'pt-br' : 'en',
      open: false,
      settings: [],
      scheduledActivities: {},
    };
  },

  computed: {
    valueType() {
      if (typeof this.date === 'object') {
        return 'date';
      }

      return this.returnFormat;
    },

    /**
     * The locale format.
     */
    localeFormat() {
      if (this.type === 'time') {
        return dayjs().getFormat('LT');
      }

      if (this.type === 'date') {
        return dayjs().getFormat('LD');
      }

      return null;
    },

    /**
     * The format to return dates
     */
    returnFormat() {
      if (this.type === 'time') {
        return 'HH:mm';
      }

      if (this.type === 'date') {
        return 'YYYY-MM-DD';
      }

      if (this.type === 'datetime') {
        return 'YYYY-MM-DD HH:mm';
      }

      return null;
    },
  },

  methods: {
    getSelectedStringDate(date) {
      let selectedDate = this.type === 'time' ? this.$parent.schedule.date : date;

      if (typeof selectedDate !== 'string') {
        selectedDate = `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1 < 10 ? '0' + (selectedDate.getMonth() + 1) : selectedDate.getMonth() + 1}-${selectedDate.getDate() < 10 ? '0' + selectedDate.getDate() : selectedDate.getDate()}`;
      } else {
        selectedDate = selectedDate.split(' ')[0];
      }

      return selectedDate;
    },

    async loadScheduledActivities(date) {
      const selectedDate = this.getSelectedStringDate(date);
      const filter = {
        users: this.users,
        deal_id: this.dealId,
        lead_hashid: this.leadHashid,
        date: selectedDate
      };
      
      try {
        const { data } = await this.$http.get('/schedule/activities', { 
          params: { filter } 
        });

        data.activities.forEach(activity => {
          this.scheduledActivities[activity.follower_id] = this.scheduledActivities[activity.follower_id] ? this.scheduledActivities[activity.follower_id] : [];
          const activityExists = this.scheduledActivities[activity.follower_id].find(act => act.activityId === activity.id);
          if (!activityExists) {
            this.scheduledActivities[activity.follower_id].push({
              activityId: activity.id,
              scheduledAt: activity.scheduled_at,
              completedAt: activity.completed_at,
            });
          }
        });

        this.settings = data.followersSettings;
        
        this.isTimeSlotAvailableForAllFollowers(selectedDate);
      } catch (error) {
        console.error("Error getting activities", error);
      }
    },

    isTimeSlotAvailableForAllFollowers(date) {
      const dayOfWeek = dayjs(date).day();
      const dayKey = this.getDayKey(dayOfWeek);
      const timeStr = dayjs(date).format('HH:mm');
      const duration = moment.duration({minutes: 15});

      //Checking available days 
      if (this.type === 'date') {
        const isDayAvailable = this.settings.every(followerSettings => {
          return followerSettings.weekDays[dayKey];
        });
        return isDayAvailable;
      }

      let isTimeAlreadyScheduled = false;

      // Calculates the intersection of the times of all followers
      this.settings.forEach((followerSettings, index) => {
        // Se o usuário puder agendar fora do horário, marca que ele pode agendar e pula para o próximo usuário
        if (followerSettings.allowOutsideHours && followerSettings.allowOverlap) {
          followerSettings.canSchedule = true;
          return;
        }

        // Se o usuário não puder agendar fora do horário, marca que ele não pode agendar e verifica se ele pode agendar no horário
        followerSettings.canSchedule = false;
        
        for (let slot of followerSettings.openingHours) {
          if (timeStr >= slot.start && timeStr <= moment(slot.end, 'HH:mm').subtract(duration).format('HH:mm')) {
            followerSettings.canSchedule = true;
            break;
          }
        }

        // Verifica se a atividade está agendada para o usuário
        isTimeAlreadyScheduled = this.scheduledActivities[followerSettings.id]?.some(activity => {
          const activityDate = dayjs(activity.scheduledAt).format('YYYY-MM-DD');
          const activityTime = dayjs(activity.scheduledAt).format('HH:mm');
          const formattedSelectedDate = dayjs(date).format('YYYY-MM-DD');

          const result = activityDate === formattedSelectedDate && activityTime === timeStr;

          return result && !followerSettings.allowOverlap;
        });
      });

      // Checks if the time is within the common period
      const everyoneCanSchedule = this.settings.every(f => f.canSchedule);

      // If it is not within the common schedule, check if EVERYONE allows outside hours
      if (!everyoneCanSchedule) {
        const allAllowOutsideHours = this.settings.every(f => f.allowOutsideHours);
        if (!allAllowOutsideHours) {
          return false;
        }
      }

      const allAllowOverlap = this.settings.every(f => f.allowOverlap);
      if (allAllowOverlap) {
        return true;
      }

      // O código abaixo tem um erro, pois ele não está verificando se a atividade está agendada para o mesmo usuário. Ou seja, os compromissos não estão sendo verificados por usuários e, sim, como um todo. 
      // Isso significa que, se o usuário 1 marcou uma atividade para às 8, quando o usuário 2 quiser marcar para o mesmo horário, verá que o horário está ocupado.
      // O correto seria verificar por usuário e não como um todo.


      return !isTimeAlreadyScheduled;
    },

    disabledDate(date) {
      if (this.future && date < dayjs().startOf('day')) {
        return true;
      }

      return !this.isTimeSlotAvailableForAllFollowers(date);
    },

    getDayKey(dayNumber) {
      return [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday'
      ][dayNumber];
    },

    disabledTime(date) {
      if (!date) return true;

      const time = dayjs(date).format('HH:mm');

      const selectedDate = this.getSelectedStringDate(this.date);

      return !this.isTimeSlotAvailableForAllFollowers(selectedDate + ' ' + time);
    },

    change(value, type) {
      if (type === 'minute') {
        this.open = false;
      }

      let formatted;
      const format = this.returnFormat;

      if (format) {
        formatted = dayjs(value).format(format);
      }

      if (formatted.toString() !== 'Invalid Date') {
        value = formatted;
      }

      if (this.type === 'date') {
        this.loadScheduledActivities(value);
      }

      this.$emit('input', value);
    },
  },
};
</script>

<style lang="scss">
@import '../../../../node_modules/vue2-datepicker/scss/index.scss';

.mx-calendar,
.mx-table,
.mx-date-row {
  font-family: Montserrat, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, 'Noto Sans', sans-serif;
}

.mx-datepicker {
  width: auto;
}

.mx-calendar-header-label {
  .mx-btn.mx-btn-text.mx-btn-current-month,
  .mx-btn.mx-btn-text.mx-btn-current-year {
    color: #3b334a;
    font-weight: 700;
    letter-spacing: 0.26px;
  }
}

.mx-btn.mx-btn-text.mx-btn-icon-double-left,
.mx-btn.mx-btn-text.mx-btn-icon-left,
.mx-btn.mx-btn-text.mx-btn-icon-double-right,
.mx-btn.mx-btn-text.mx-btn-icon-right {
  color: #3b334a;
}

.mx-table.mx-table-date th {
  color: #3b334a;
  font-weight: 700;
  letter-spacing: 0.22px;
}

.mx-icon-calendar {
  display: none;
}

.mx-date-row {
  .cell {
    color: #3b334a;
  }

  .cell.active {
    background-color: #7123bd;
    border-radius: 15px;
  }

  .cell.disabled {
    background-color: #fff;
  }
}

.mx-time-column .mx-time-list::after {
  height: 5px;
}
</style>
